import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropdownOptionComponent } from './dropdown-option.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

export interface DropdownOption {
  label: string;
  value: any;
  /** Optional Font Awesome class string, e.g. 'fa fa-calendar' or 'fa fa-fw fa-user' */
  icon?: string;
}

@Component({
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ScrollingModule,
    CdkVirtualScrollViewport
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true,
    },
  ],
})
export class CustomDropdownComponent implements OnInit, OnChanges, AfterContentInit, ControlValueAccessor {
  /**
   * List of options. Accepts either plain strings or { label, value } objects.
   * When strings are passed they are used as both the display label and the emitted value.
   */
  @Input() options: Array<DropdownOption | string> = [];

  /** The currently selected value. Supports two-way binding: [(value)]="myVar" */
  @Input() value: any = null;

  /** Placeholder shown when no option is selected. */
  @Input() placeholder = 'Select...';

  /** Font Awesome class string to show in the trigger when no value is selected, e.g. 'fa fa-fw fa-list' */
  @Input() placeholderIcon: string | null = null;

  /** When true, a clear (×) button appears in the trigger whenever a value is selected. */
  @Input() clearable = false;

  /** When true, a search input appears at the top of the panel to filter options. */
  @Input() filterable = false;

  /** Placeholder text for the filter input. */
  @Input() filterPlaceholder = 'Search...';

  /** When true, disables all interaction. Works with [disabled] binding and reactive-forms setDisabledState. */
  @Input()
  set disabled(val: boolean) {
    this.isDisabled = !!val;
    if (this.isDisabled) { this.isOpen = false; this.filterQuery = ''; }
  }
  get disabled(): boolean { return this.isDisabled; }

  /** When true, removes the trigger border and background ("ghost" appearance). The panel renders normally. */
  @Input() naked = false;

  /** Emits the selected value — enables [(value)] two-way binding in the parent. */
  @Output() valueChange = new EventEmitter<any>();

  /** Emits the full { label, value } option on every selection change. */
  @Output() selectionChange = new EventEmitter<DropdownOption>();

  /** Emits null when the user clears the selection. */
  @Output() cleared = new EventEmitter<void>();

  /**
   * Declarative option children — populated when the consumer uses
   * <app-dropdown-option> elements inside <app-custom-dropdown>.
   * Takes priority over the [options] input when present.
   */
  @ContentChildren(DropdownOptionComponent, { descendants: true })
  contentOptions: QueryList<DropdownOptionComponent> = new QueryList<DropdownOptionComponent>();

  isOpen = false;
  filterQuery = '';
  isDisabled = false;
  /** When true the panel is anchored to the right edge of the trigger so it doesn't overflow the screen. */
  panelAlignRight = false;

  @HostBinding('class.cd-disabled') get hostDisabledClass() { return this.isDisabled; }
  @HostBinding('attr.aria-disabled') get hostAriaDisabled() { return this.isDisabled || null; }

  /** Normalised internal option list. */
  normalizedOptions: DropdownOption[] = [];

  /** Height in px of a single option row — must match $row-height in the SCSS. */
  readonly rowHeight = 32;
  /** Maximum number of rows visible before the virtual viewport scrolls. */
  readonly maxVisibleRows = 8;

  /** Dynamic height for the CDK virtual scroll viewport. */
  get viewportHeight(): number {
    return Math.min(this.visibleOptions.length, this.maxVisibleRows) * this.rowHeight;
  }

  /** trackBy for *cdkVirtualFor — avoids re-rendering unchanged rows. */
  trackByValue(_index: number, option: DropdownOption): any {
    return option.value;
  }

  // ── ControlValueAccessor callbacks ────────────────────────────────────────
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  /** Options filtered by the current filterQuery. */
  get visibleOptions(): DropdownOption[] {
    if (!this.filterable || !this.filterQuery.trim()) {
      return this.normalizedOptions;
    }
    const q = this.filterQuery.trim().toLowerCase();
    return this.normalizedOptions.filter(o => o.label.toLowerCase().includes(q));
  }

  /** The label to show in the trigger for the current value. */
  get selectedLabel(): string {
    if (this.value === null || this.value === undefined) {
      return this.placeholder;
    }
    const match = this.normalizedOptions.find(o => o.value === this.value);
    return match ? match.label : this.placeholder;
  }

  /** The icon class to show in the trigger. Falls back to placeholderIcon when nothing is selected. */
  get selectedIcon(): string | null {
    if (this.value === null || this.value === undefined) {
      return this.placeholderIcon || null;
    }
    const match = this.normalizedOptions.find(o => o.value === this.value);
    return (match && match.icon) ? match.icon : (this.placeholderIcon || null);
  }

  /** True when at least one option carries an icon (or a placeholderIcon is set). */
  get hasIcons(): boolean {
    return !!this.placeholderIcon || this.normalizedOptions.some(o => !!o.icon);
  }

  constructor(private elRef: ElementRef) {}

  ngOnInit(): void {
    this.buildOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.buildOptions();
    }
  }

  ngAfterContentInit(): void {
    // Initial sync from content children (if any were projected)
    this.buildOptions();
    // Re-sync whenever the *ngFor produces a different set of options
    this.contentOptions.changes.subscribe(() => this.buildOptions());
  }

  private buildOptions(): void {
    // Content children (<app-dropdown-option>) take priority over [options] input
    if (this.contentOptions && this.contentOptions.length > 0) {
      this.normalizedOptions = this.contentOptions.toArray().map(o => ({
        label: o.label,
        value: o.value,
        icon: o.icon || undefined,
      }));
    } else {
      this.normalizedOptions = (this.options || []).map(o =>
        typeof o === 'string' ? { label: o, value: o } : o
      );
    }
  }

  // ── ControlValueAccessor implementation ───────────────────────────────────

  /** Called by Angular forms to push a new value into the component. */
  writeValue(value: any): void {
    this.value = value !== undefined ? value : null;
  }

  /** Called by Angular forms to register the change callback. */
  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  /** Called by Angular forms to register the touched callback. */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** Called by Angular forms when the disabled state changes. */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
  }

  // ── Interaction ───────────────────────────────────────────────────────────

  toggle(): void {
    if (this.isDisabled) { return; }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // After the panel renders, check whether it spills past the right edge of the
      // viewport and flip it to right-aligned if it does.
      this.panelAlignRight = false;
      setTimeout(() => {
        const host = this.elRef.nativeElement as HTMLElement;
        const rect = host.getBoundingClientRect();
        // Estimate panel width: use its rendered width if available, else fall back to
        // the host width (before CDK sets max-content). The panel cannot exceed 100vw.
        const panel = host.querySelector('.cd-panel') as HTMLElement | null;
        const panelWidth = panel ? panel.offsetWidth : host.offsetWidth;
        if (rect.left + panelWidth > window.innerWidth) {
          this.panelAlignRight = true;
        }
      }, 0);
    } else {
      this.panelAlignRight = false;
      this.filterQuery = '';
      this._onTouched();
    }
  }

  select(option: DropdownOption): void {
    this.value = option.value;
    this._onChange(option.value);
    this._onTouched();
    this.valueChange.emit(option.value);
    this.selectionChange.emit(option);
    this.isOpen = false;
    this.filterQuery = '';
  }

  clearSelection(event: MouseEvent): void {
    if (this.isDisabled) { return; }
    event.stopPropagation();
    this.value = null;
    this._onChange(null);
    this._onTouched();
    this.valueChange.emit(null);
    this.cleared.emit();
    this.isOpen = false;
    this.filterQuery = '';
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (!this.elRef.nativeElement.contains(target)) {
      if (this.isOpen) {
        this._onTouched();
      }
      this.isOpen = false;
      this.filterQuery = '';
    }
  }
}
